// Phase 81vv — Bottom Navigation 5 items + active orange glow
// Phase 83y.27 — iOS Safari fix (transform-gpu pin to viewport bottom).
// Phase 83y.28 — Pixel-perfect alignment redesign:
//   • Indicator nằm trong từng tab item (không dùng shared layoutId),
//     đảm bảo căn giữa tuyệt đối với icon + label, không drift sang trái/phải.
// Phase 84.M.68 — Icon "Trận đấu" đổi sang `CrossPaddlesIcon` (2 vợt
//   pickleball giao nhau + bóng nhỏ) — biểu tượng đối đầu thay cho
//   PickleballSpinIcon cũ. Đồng thời mở rộng quyền GUEST: tab Trận đấu,
//   Xếp hạng, Cá nhân đều `gated: false` (chỉ pop modal ở action sâu).
import { Link, useLocation, useNavigate } from "react-router-dom";
import { House, Trophy, ChartBar, User } from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import { useGuestGate } from "../../contexts/GuestLoginGateContext";

// Phase 84.M.69 — Cross paddles icon (FINAL v3 — simplified per user spec).
// Geometry:
//   • Ball circle (r=1.8) tại (12, 3.5) ở top.
//   • 5 dots pickleball (chỉ khi active) trong ball.
//   • 2 paddle heads = 2 rounded squares (rect 5×6, rx=2) tại NW(4.5,5)
//     và NE(14.5,5) — KHÔNG rotate để tránh blob.
//   • 2 handle lines chéo nhau hình X: paddle A handle từ NW-head-bottom
//     (8, 11) đi xuống SE (15, 20). Paddle B từ NE-bottom (16, 11) xuống
//     SW (9, 20). Cross point ~(11.5, 17).
//
// Active: stroke gradient cam #FFB347 → #FF6A00 + dots fill grad.
// Inactive: stroke currentColor (white/gray), không vẽ dots cho thoáng.
let __crossPaddleGradId = 0;
function CrossPaddlesIcon({ size = 22, weight = "regular", className = "", style }) {
  const isActive = weight === "fill";
  const gradId = `cp-grad-${++__crossPaddleGradId}`;
  const strokeRef = isActive ? `url(#${gradId})` : "currentColor";
  const fillRef = isActive ? `url(#${gradId})` : "currentColor";
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      style={style}
      fill="none"
      stroke={strokeRef}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {isActive && (
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="24" y2="24">
            <stop offset="0%" stopColor="#FFB347" />
            <stop offset="100%" stopColor="#FF6A00" />
          </linearGradient>
        </defs>
      )}
      {/* Ball ở trên center */}
      <circle cx="12" cy="3.5" r="1.6" />
      {/* 5 dots pickleball — chỉ khi active */}
      {isActive && (
        <g fill={fillRef} stroke="none">
          <circle cx="11.1" cy="2.9" r="0.3" />
          <circle cx="12.9" cy="2.9" r="0.3" />
          <circle cx="10.7" cy="3.8" r="0.3" />
          <circle cx="13.3" cy="3.8" r="0.3" />
          <circle cx="12" cy="4.2" r="0.3" />
        </g>
      )}
      {/* Paddle A — full paddle (head + handle) as 1 path. Heads tilt
          INWARD chữ V (heads gần nhau ở giữa-trên, handles spread out
          chéo nhau hình X xuống dưới). Pivot ở head center, rotate +35°
          để paddle A chéo từ NE-head xuống SW-handle. */}
      <g transform="rotate(35 7 9)">
        <path d="M4 6 Q4 5 5 5 L9 5 Q10 5 10 6 L10 11 Q10 12 9 12 L8 12 L8 18 L6 18 L6 12 L5 12 Q4 12 4 11 Z" />
      </g>
      {/* Paddle B — gương ảnh, rotate -35° quanh (17, 9). */}
      <g transform="rotate(-35 17 9)">
        <path d="M14 6 Q14 5 15 5 L19 5 Q20 5 20 6 L20 11 Q20 12 19 12 L18 12 L18 18 L16 18 L16 12 L15 12 Q14 12 14 11 Z" />
      </g>
    </svg>
  );
}

const ITEMS = [
  // Phase 84.M.68 — Mở quyền guest cho 3 tab: matches/ranking/me (gated=false).
  // Deep actions (chi tiết trận, livestream, hồ sơ VĐV khác) vẫn cần login —
  // route-level ProtectedRoute hoặc click handler `requireAuth()` xử lý.
  { key: "home", label: "Trang chủ", to: "/", icon: House, gated: false },
  { key: "matches", label: "Trận đấu", to: "/athlete/matches", icon: CrossPaddlesIcon, gated: false },
  { key: "tournaments", label: "Giải đấu", to: "/athlete/tournaments", icon: Trophy, gated: false },
  { key: "ranking", label: "Xếp hạng", to: "/athlete/ranking", icon: ChartBar, gated: false },
  { key: "me", label: "Cá nhân", to: "/athlete/profile", icon: User, gated: false },
];

export default function BottomNav() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { requireAuth } = useGuestGate();
  return (
    <nav
      // Phase 83y.27 — iOS Safari fix: transform: translateZ(0) forces the
      // element onto its own compositor layer so it stays pinned to the
      // viewport bottom across Safari toolbar transitions.
      className="fixed bottom-0 left-0 right-0 z-50 bg-[#060B14]/95 backdrop-blur-xl border-t border-white/8 transform-gpu will-change-transform"
      style={{ transform: "translateZ(0)" }}
      data-testid="home-bottom-nav"
    >
      <div
        className="flex"
        style={{
          // Phase 84.M.107 — Padding bottom dùng max(env, --pl-safe-bottom)
          // để hỗ trợ cả iOS Safari (env) và Android WebView (CSS var inject).
          paddingBottom: "max(env(safe-area-inset-bottom, 8px), var(--pl-safe-bottom))",
        }}
        // Phase 83y.28 — Equal-width tabs via flex. Grid + content widths
        // mixed differently across icons (Trophy is narrower than House),
        // which Framer's shared layoutId interpolated and made the
        // indicator appear off-center. Plain flex-1 = same column for all.
      >
        {ITEMS.map((item) => {
          const Icon = item.icon;
          const active =
            (item.key === "home" && (loc.pathname === "/" || loc.pathname === "")) ||
            (item.key !== "home" && loc.pathname.startsWith(item.to));
          // Phase 84.M.38 — Khi guest click vào tab gated → mở Gate modal,
          // không navigate.
          const needsGate = item.gated && !user;
          const handleClick = needsGate
            ? (e) => {
                e.preventDefault();
                requireAuth(() => navigate(item.to), { hint: item.hint });
              }
            : undefined;
          return (
            <Link
              key={item.key}
              to={item.to}
              onClick={handleClick}
              className="relative flex-1 flex flex-col items-center justify-end gap-1 pt-3 pb-2"
              data-testid={`home-nav-${item.key}`}
            >
              {/* Phase 83y.28 — Indicator rendered INSIDE each tab so it's
                  guaranteed to sit on the tab's own horizontal center.
                  Fade in/out instead of sliding between tabs so we can't
                  drift while transitioning. */}
              <span
                aria-hidden
                className={`absolute top-0 left-1/2 -translate-x-1/2 w-8 h-1 rounded-full bg-[#FF8A00] transition-opacity duration-200 ${
                  active ? "opacity-100" : "opacity-0"
                }`}
                style={
                  active
                    ? { boxShadow: "0 0 12px rgba(255,138,0,0.6)" }
                    : undefined
                }
              />
              <Icon
                size={22}
                weight={active ? "fill" : "regular"}
                className={active ? "text-[#FF8A00]" : "text-[#94A3B8]"}
                style={
                  active
                    ? { filter: "drop-shadow(0 0 8px rgba(255,138,0,0.45))" }
                    : undefined
                }
              />
              <span
                className={`text-[10px] font-semibold leading-none ${
                  active ? "text-[#FF8A00]" : "text-[#94A3B8]"
                }`}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
